import React, { useState } from "react";
import {
  Box,
  TextField,
  Card,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";

const StoresList = ({ stores, selectedStoreId, onSelectStore }) => {
  const [query, setQuery] = useState("");

  const filteredStores = stores.filter((store) =>
    `${store.name} ${store.address} ${store.city} ${store.province}`
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 1,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder="Search by city, name, or address..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.75rem",
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
            "&:hover fieldset": { borderColor: "var(--primary-blue)" },
            "&.Mui-focused fieldset": { borderColor: "var(--primary-blue)" },
          },
        }}
      />

      {/* Scrollable list */}
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {filteredStores.map((store) => {
          const isSelected = store.id === selectedStoreId;

          const hours = store.operatingHours
            ? store.operatingHours.replace(/-/g, " - ").split(",")
            : [];

          return (
            <Card
              key={store.id}
              onClick={() => onSelectStore(store.id)}
              sx={{
                p: 3,
                flexShrink: 0,
                borderRadius: "1.25rem",
                background: isSelected
                  ? "linear-gradient(145deg, rgba(232, 245, 255, 1), rgba(255,255,255,1))"
                  : "linear-gradient(145deg, rgba(255,255,255,1), rgba(245,250,255,1))",
                border: `1px solid ${
                  isSelected ? "var(--primary-blue)" : "rgba(0,0,0,0.08)"
                }`,
                boxShadow: isSelected
                  ? "0 8px 24px rgba(0, 0, 0, 0.12)"
                  : "0 4px 14px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.02)",
                  boxShadow: "0 8px 28px rgba(0, 0, 0, 0.12)",
                  borderColor: "var(--primary-blue)",
                },
              }}
            >
              {/* Main heading (constant) */}
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "var(--secondary-blue)", mb: 0.5 }}
              >
                Skymart
              </Typography>

              {/* Subtitle from JSON */}
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  mb: 1,
                  fontStyle: "italic",
                }}
              >
                {store.name}
              </Typography>

              {/* Address */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ mb: 0.5 }}
              >
                <LocationOnIcon
                  fontSize="small"
                  sx={{ color: "var(--secondary-blue)" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {`${store.address}, ${store.city}, ${store.province} ${store.postalCode}`}
                </Typography>
              </Stack>

              {/* Phone */}
              {store.phone && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <PhoneIcon
                    fontSize="small"
                    sx={{ color: "var(--secondary-blue)" }}
                  />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {store.phone}
                  </Typography>
                </Stack>
              )}

              {/* Operating Hours */}
              {hours.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    {hours.map((h, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{ color: "text.secondary", lineHeight: 1.4 }}
                      >
                        {h.trim()}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default StoresList;
